import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Volume2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, getChildId } from "../../lib/api";
import { play, sfx } from "../../lib/audio";
import { useArt } from "../../lib/art";
import { ArtShell } from "../../components/art/ArtShell";
import { DrawingBoard } from "../../components/art/DrawingBoard";
import { SaveDialog } from "../../components/art/SaveDialog";

const MODE = { color: "color", culture: "color", follow: "trace", character: "trace" };

export default function ArtStudio() {
  const { activityId } = useParams();
  const nav = useNavigate();
  const childId = getChildId();
  const [meta] = useArt();
  const [act, setAct] = useState(activityId ? null : { title: "自由畫板", prompt: "想畫什麼就畫什麼！", kind: "free" });
  const [blob, setBlob] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activityId) return;
    api.get(`/art/activities/${activityId}`, { params: { child_id: childId } })
      .then((r) => { setAct(r.data); setTimeout(() => play(r.data.prompt), 500); })
      .catch((e) => { if (e?.response?.status === 402) toast.error("這個活動是 Premium 內容"); nav("/art"); });
  }, [activityId, childId, nav]);

  const confirm = async (form) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("child_id", childId);
    if (activityId) fd.append("activity_id", activityId);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", blob, "art.png");
    try {
      const r = await api.post("/art/works", fd);
      sfx("good");
      toast.success(r.data.star_awarded ? "作品放進作品集了！得到一顆星星 ⭐" : "作品放進作品集了！");
      nav("/art/works");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "保存失敗，請再試一次", e?.response?.status === 402 ? { action: { label: "查看方案", onClick: () => nav("/pricing") } } : undefined);
    } finally { setBusy(false); }
  };

  if (!act) return <ArtShell title="…" testid="art-studio-page" />;
  return (
    <ArtShell title={act.title} testid="art-studio-page">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button data-testid="art-prompt-btn" onClick={() => play(act.prompt)} className="h-12 px-4 rounded-full bg-white border-2 border-[#f3d9b8] flex items-center gap-2 text-lg text-stone-700"><Volume2 className="text-[#E2683C]" />{act.prompt}</button>
        {act.story && <button data-testid="art-story-btn" onClick={() => play(act.story)} className="h-12 px-5 rounded-full bg-[#B4471F] text-white flex items-center gap-2 text-lg font-bold"><BookOpen />聽聽典故</button>}
      </div>
      {act.story && <p className="mb-4 max-w-3xl text-stone-600 text-base leading-relaxed bg-white/70 rounded-2xl p-4 border border-[#f3d9b8]" data-testid="art-story-text">{act.story}</p>}
      <DrawingBoard lineUrl={act.line_url} mode={MODE[act.kind] || "free"} onSave={setBlob} saving={busy} />
      {blob && meta && <SaveDialog open onOpenChange={(o) => !o && setBlob(null)} defaultTitle={act.kind === "free" ? "我的畫" : act.title} frames={meta.frames} full={meta.full} onConfirm={confirm} busy={busy} />}
    </ArtShell>
  );
}
